import { ProcessUrlResponse } from '../types';
import { ObservableMap } from '../lib/map/observableMap';
import { processUrl } from '../actions/accountActions';
import { VERDICT_TIMEOUT_MS } from './verdictTimeout';
import { WebEvent } from '../types/api/events';
import { getDeviceAndUserStore } from '../storage/stores';
import { now } from '../lib/dateTime';
import { isOverDailyQuota } from '../validators/timeQuotaValidator';
import { isScheduleRestricted } from '../validators/timeScheduleValidator';

export type ProcessedVerdict = {
  verdict: ProcessUrlResponse;
  webEvent?: WebEvent;
  requestId?: string;
};

// List of processed verdicts
const verdictCache = new ObservableMap<string, ProcessedVerdict>();

/**
 * Returns the verdict for a navigation.
 *
 * Verdicts are pre-processed when receiving an HTTP(s) event on {@link processNetworkRequest},
 * taking into consideration the LW verdict if applies.
 *
 * If the verdict is already available, it will return it.
 * If the verdict is still being pre-processed, will return it as soon as it's received, with a maximum wait of 5 seconds.
 * If the verdict is not being pre-processed, or didn't finish pre-processing it in time, it will process it and return it right away.
 */
export const getVerdict = async (
  url: string,
  skipCache = false
): Promise<ProcessedVerdict> => {
  const cachedVerdict = skipCache ? undefined : verdictCache.get(url);

  // Verdict is not being-pre processed or available
  if (!cachedVerdict) {
    return await processVerdict(url);
  }

  // Verdict it's already available
  if (isVerdictAvailable(cachedVerdict.verdict)) {
    return cachedVerdict;
  }

  // Return verdict when available, if not undefined. Or process it if not.
  const newVerdict = await verdictCache.waitForValue(
    url,
    value => isVerdictAvailable(value?.verdict),
    VERDICT_TIMEOUT_MS
  );
  return newVerdict ?? (await processVerdict(url));
};

const isVerdictAvailable = (verdict?: ProcessUrlResponse) =>
  verdict !== undefined && verdict !== ProcessUrlResponse.PENDING;

const processVerdict = async (url: string) => {
  // Always call processUrl first to get the base verdict
  const baseVerdict = await processUrl(url);

  // Check if time-based blocking should override the base verdict. In theory,
  // we should be able to check the blockedByTime verdict as first step, but
  // it's not possible to do so right now because is the processUrl function that
  // returns a full ProcessedVerdict object with a webEvent, which is needed
  // to be able to report the event.
  // CHR-347: Temporarily enable logs so we can debug production issues.
  // Only logs comming from here should be printed to avoid spam,
  // as this method is called very often from other places.
  const timeBlockResult = await shouldBlockByTime({ shouldLog: true });
  const verdict = timeBlockResult
    ? { ...baseVerdict, verdict: timeBlockResult }
    : baseVerdict;

  saveVerdict(url, verdict);
  return verdict;
};

export const getVerdictByRequestId = (
  requestId: string
): Promise<ProcessedVerdict | undefined> => {
  for (const [key, value] of verdictCache.entries()) {
    if (value.requestId === requestId) {
      return getVerdict(key);
    }
  }
  return Promise.resolve(undefined);
};

export const saveVerdict = (url: string, verdict: ProcessedVerdict) =>
  verdictCache.set(url, verdict);

export const setPendingVerdict = (url: string) =>
  verdictCache.set(url, { verdict: ProcessUrlResponse.PENDING });

export const shouldBlockByTime = async ({
  shouldLog = false,
}: { shouldLog?: boolean } = {}) => {
  const deviceUser = getDeviceAndUserStore().get();
  if (!deviceUser) {
    if (shouldLog) {
      console.log('[shouldBlockByTime] UNDEFINED - No device user');
    }
    return undefined;
  }

  const currentDate = await now();
  if (isOverDailyQuota(deviceUser, currentDate, undefined, { shouldLog })) {
    if (shouldLog) {
      console.log(
        '[shouldBlockByTime] BLOCK_BY_QUOTA - Device is over daily quota'
      );
    }
    return ProcessUrlResponse.BLOCK_BY_QUOTA;
  }

  if (isScheduleRestricted(currentDate, { shouldLog })) {
    if (shouldLog) {
      console.log(
        '[shouldBlockByTime] BLOCK_BY_SCHEDULE - Device is over schedule'
      );
    }
    return ProcessUrlResponse.BLOCK_BY_SCHEDULE;
  }
};
